import { useId } from 'react';

import { cx } from 'utils';

/**
 * LogoMark — the standalone LearnX glyph.
 *
 * An indigo rounded tile containing a clean white "spark" mark formed by two
 * crossing paths that create an X at their centre. The brighter diagonal
 * represents student growth; the softer diagonal represents the mentor's
 * guidance. A small white circle at the crossing point marks the moment of
 * learning — the platform's core purpose.
 *
 * Scales cleanly from 16 px (favicon) to 96 px (hero). `useId` keeps gradient
 * ids unique so multiple instances can render on the same page.
 */
export function LogoMark({ size = 32, className = '', glow = false, ...props }) {
  const id = useId();
  const g = `lx-${id}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label="LearnX"
      className={cx(glow && 'drop-shadow-[0_4px_20px_rgba(79,70,229,0.40)]', className)}
      {...props}
    >
      <defs>
        {/* Indigo tile gradient — top-left lighter, bottom-right deeper */}
        <linearGradient id={`${g}-bg`} x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop stopColor="#6366F1" />
          <stop offset="1" stopColor="#4F46E5" />
        </linearGradient>
        {/* Subtle inner highlight for depth */}
        <radialGradient id={`${g}-shine`} cx="30%" cy="25%" r="55%" gradientUnits="objectBoundingBox">
          <stop stopColor="#FFFFFF" stopOpacity="0.18" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Indigo tile */}
      <rect width="64" height="64" rx="15" fill={`url(#${g}-bg)`} />
      {/* Subtle inner shine — not glass, just depth */}
      <rect width="64" height="64" rx="15" fill={`url(#${g}-shine)`} />
      {/* Hairline border ring */}
      <rect
        x="0.75" y="0.75"
        width="62.5" height="62.5"
        rx="14.25"
        stroke="#FFFFFF"
        strokeOpacity="0.15"
        strokeWidth="1.5"
      />

      {/* Primary diagonal — growth / upward trajectory */}
      <line x1="19" y1="19" x2="45" y2="45" stroke="#FFFFFF" strokeWidth="6.5" strokeLinecap="round" />
      {/* Secondary diagonal — mentorship / guidance */}
      <line x1="45" y1="19" x2="19" y2="45" stroke="#FFFFFF" strokeWidth="6.5" strokeOpacity="0.55" strokeLinecap="round" />
      {/* Intersection spark — the learning moment */}
      <circle cx="32" cy="32" r="4.5" fill="#FFFFFF" fillOpacity="0.95" />
      <circle cx="32" cy="32" r="2"   fill={`url(#${g}-bg)`} />
    </svg>
  );
}

/**
 * Logo — full lockup (mark + wordmark).
 *
 * @param {'full'|'mark'} [variant='full']
 * @param {'sm'|'md'|'lg'} [size='md']
 * @param {boolean} [glow=false]
 * @param {'dark'|'light'} [tone='dark']  Wordmark colour — `light` on dark backgrounds.
 */
const SIZES = {
  sm: { mark: 24, text: 'text-lg' },
  md: { mark: 32, text: 'text-xl' },
  lg: { mark: 44, text: 'text-display-xs' },
};

export default function Logo({
  variant = 'full',
  size = 'md',
  glow = false,
  tone = 'dark',
  className = '',
  ...props
}) {
  const s = SIZES[size] || SIZES.md;
  const light = tone === 'light';

  return (
    <span className={cx('inline-flex select-none items-center gap-2.5', className)} {...props}>
      <LogoMark size={s.mark} glow={glow} className="shrink-0" />
      {variant === 'full' && (
        <span
          className={cx(
            'font-display font-bold leading-none tracking-tight',
            light ? 'text-white' : 'text-slate-900',
            s.text
          )}
        >
          Learn<span className={light ? 'text-brand-300' : 'text-brand-600'}>X</span>
        </span>
      )}
    </span>
  );
}
